import { get, IHttpRequestConfig, post, put, remove } from "@/browser";
import { applyHeaders, extractDataFromResponse } from "@/core/api";
import { compose } from "@/core/url";
import {
  consultantProfileType,
  IConsultantProfile,
  IConsultantProfileRolePayload,
  TCompletenessItemKey,
} from "@/data/consultant-profiles";
import { TLegalDocumentType } from "@/data/legal-documents";
import { ICreateCompanyFormValues } from "@/views/components/Company/Create";

import { IPartialPageableUser, IUser, pageableUserType, userType } from "./types-ensure";

const API = {
  list: "/api/users",
  put: "/api/users/{userId}",
  delete: "/api/users/{userId}",
  state: "/api/consultant-profiles/states",
  connectivity: "/api/consultant-profiles/connectivity-info/{profileId}",
  role: `/api/consultant-profiles/{profileId}/role`,
  updateLegalDocuments: "/api/users/current/documents",
  resetAssessments: "/api/user-assessments/reset",
  markPrimaryEmail: "/api/users/email/mark-as-primary",
  assignEmailToConsultantProfile: "/api/consultant-profiles/{profileId}/email",
  email: "/api/users/email",
  password: "/api/users/password",
  lastUsedConsultantProfile: "/api/users/last-used-consultant",
  convert: "/api/users/convert/{profileId}",
  joinCompany: "/api/users/join-company",
};

export enum TUnableToCloseAccountCodes {
  COMPANY_LAST_ADMIN_PROFILE = "COMPANY_LAST_ADMIN_PROFILE",
}

export enum TAbleToCloseAccountCode {
  LAST_PROFILE = "LAST_PROFILE",
  PERSONAL_PROFILE = "PERSONAL_PROFILE",
  COMPANY_CONSULTANT_PROFILE = "COMPANY_CONSULTANT_PROFILE",
  COMPANY_OWN_MANAGER_PROFILE = "COMPANY_OWN_MANAGER_PROFILE",
  COMPANY_MANAGER_PROFILE = "COMPANY_MANAGER_PROFILE",
}

export type TCloseAccountCodes = keyof TUnableToCloseAccountCodes | keyof TAbleToCloseAccountCode;

export interface IFetchUsersListParams {
  companyId: number;
  userRole?: "MANAGER";
  unpaged?: boolean;
}

export const fetchUsersList = (params: IFetchUsersListParams, options: IHttpRequestConfig = {}) => {
  const { companyId, ...searchParams } = params;
  const url = compose(API.list, {}, searchParams);
  return get<IPartialPageableUser>(url, applyHeaders({ companyId }, options))
    .then(extractDataFromResponse)
    .then(pageableUserType);
};

export interface IProfileRoleConsultantProfileParams {
  companyId?: number;
  profileId: number;
}

export const updateProfileRole = (
  params: IProfileRoleConsultantProfileParams,
  data: IConsultantProfileRolePayload,
  options?: IHttpRequestConfig,
) => {
  const { companyId, ...urlParams } = params;
  const url = compose(API.role, urlParams);
  return put<IConsultantProfile>(url, data, applyHeaders({ companyId }, options))
    .then(extractDataFromResponse)
    .then(consultantProfileType);
};

export interface IPutUserParams {
  userId: number;
}

export const putUser = (params: IPutUserParams, data: IUser, options?: IHttpRequestConfig) => {
  const url = compose(API.put, params);
  return put<IUser>(url, { ...data, language: data.language.toUpperCase() }, options)
    .then(extractDataFromResponse)
    .then(userType);
};

export interface IDeactivateUserParams {
  userId: number;
}

export interface IConvertUserParams {
  profileId: number;
}

export const deleteUser = (params: IDeactivateUserParams, options?: IHttpRequestConfig) => {
  const url = compose(API.delete, params);
  return remove<undefined>(url, {}, options).then(extractDataFromResponse);
};

export const convertUser = (
  profileId: IConvertUserParams,
  body: ICreateCompanyFormValues,
  options?: IHttpRequestConfig,
) => {
  const url = compose(API.convert, profileId);
  return post<undefined>(url, body, options).then(extractDataFromResponse);
};

interface IJoinCompanyPayload {
  companyId: number;
  applicationIds: number[];
}

export const joinCompany = (body: IJoinCompanyPayload, options?: IHttpRequestConfig) => {
  const url = compose(API.joinCompany);

  return post<undefined>(url, body, options).then(extractDataFromResponse);
};

export interface IUserConsultantProfileState {
  consultantProfileId: Pick<IConsultantProfile, "id">;
  state: TCloseAccountCodes;
}

export const fetchUserConsultantProfilesState = (_?: {}, options?: IHttpRequestConfig) => {
  const url = compose(API.state);
  return get<IUserConsultantProfileState[]>(url, options).then(extractDataFromResponse);
};

export interface IConnectivityResponse {
  hasActiveAppOrContract: boolean;
}

export const fetchProfilesConnectivityToApplicationsAndContracts = (
  profileId: number,
  options?: IHttpRequestConfig,
) => {
  const url = compose(API.connectivity, { profileId });
  return get<IConnectivityResponse>(url, options).then(extractDataFromResponse);
};

export interface IPutLegalDocuments {
  type: TLegalDocumentType;
  version: number;
}

export const putLegalDocuments = (params: undefined, data: IPutLegalDocuments[], options?: IHttpRequestConfig) => {
  const url = compose(API.updateLegalDocuments, params);
  return put<IPutLegalDocuments[]>(url, data, options);
};

export interface IResetAssessmentsParams {
  companyId?: number;
}

export interface IResetAssessmentsData {
  assessments: TCompletenessItemKey[];
}

export const resetAssessments = (
  params: IResetAssessmentsParams,
  data: IResetAssessmentsData,
  options?: IHttpRequestConfig,
) => {
  const { companyId } = params;
  return post(API.resetAssessments, data, applyHeaders({ companyId }, options));
};

export interface IEmailModificationPayload {
  email: string;
  password: string;
}

export const markEmailAsPrimary = (data: IEmailModificationPayload, options?: IHttpRequestConfig) => {
  return put<any>(API.markPrimaryEmail, data, options).then(extractDataFromResponse);
};

export const addNewUserEmail = (data: IEmailModificationPayload, options?: IHttpRequestConfig) => {
  return post<any>(API.email, data, options).then(extractDataFromResponse);
};

export const deleteUserEmail = (data: IEmailModificationPayload, options?: IHttpRequestConfig) => {
  return remove<any>(API.email, data, options).then(extractDataFromResponse);
};

export const assignEmailToConsultantProfile =
  (params: IProfileRoleConsultantProfileParams) => (data: IEmailModificationPayload, options?: IHttpRequestConfig) => {
    const { companyId, ...urlParams } = params;
    const url = compose(API.assignEmailToConsultantProfile, urlParams);
    return post<any>(url, data, applyHeaders({ companyId }, options)).then(extractDataFromResponse);
  };

export interface IChangePasswordData {
  oldPassword: string;
  newPassword: string;
}

export const changePassword = (data: IChangePasswordData, options?: IHttpRequestConfig) => {
  return put<any>(API.password, data, options).then(extractDataFromResponse);
};

export const updateLastUsedConsultantProfile = async (lastUsedConsultantId?: number, options?: IHttpRequestConfig) =>
  await put(API.lastUsedConsultantProfile, { lastUsedConsultantId }, options);
